/**
 * Custo fixo mensal, diluído por unidade.
 *
 * Aluguel, salário, contador, software: eles existem mesmo que você não venda
 * nada no mês. Deixá-los de fora faz cada venda parecer mais lucrativa do que é
 * — e é o erro que faz alguém descobrir no fim do ano que o negócio dava
 * prejuízo enquanto todas as análises diziam "viável".
 *
 * ⚠️ **A diluição depende de uma ESTIMATIVA de volume, e é o elo mais frágil
 * desta conta.** Vender metade do previsto dobra o custo fixo por unidade. Por
 * isso o rateio nunca é apresentado como fato: ele é premissa, e a tela mostra
 * com quantas vendas ele foi calculado, para a pessoa poder discordar do número
 * em vez de descobrir tarde que discordava.
 */

export interface CustoFixo {
  nome: string;
  valorMensalCents: number;
  ativo: boolean;
}

export const totalMensalCents = (custos: readonly CustoFixo[]): number =>
  custos.filter((c) => c.ativo).reduce((s, c) => s + c.valorMensalCents, 0);

export type MotivoSemRateio =
  /** Nenhum custo fixo cadastrado — e isso é diferente de custo fixo zero. */
  | 'SEM_CUSTOS'
  /** Não há estimativa de quantas vendas por mês. Sem ela não há por quanto dividir. */
  | 'SEM_VOLUME';

export type Rateio =
  | {
      definido: true;
      /** O que cada unidade vendida precisa carregar de custo fixo. */
      porUnidadeCents: number;
      totalMensalCents: number;
      vendasMensaisEsperadas: number;
    }
  | { definido: false; motivo: MotivoSemRateio };

export const RECADO_SEM_RATEIO: Record<MotivoSemRateio, string> = {
  SEM_CUSTOS:
    'Nenhum custo fixo cadastrado. Se você tem aluguel, salário ou mensalidade, cadastre em Configurações — sem isso a margem sai otimista.',
  SEM_VOLUME:
    'Falta dizer quantas vendas por mês você espera. É por esse número que o custo fixo é dividido.',
};

/**
 * Quanto de custo fixo cada unidade vendida precisa cobrir.
 *
 * Arredonda para CIMA. Um centavo a menos por unidade, repetido mil vezes,
 * deixa a conta do mês descoberta — e o erro sempre cai para o mesmo lado.
 */
export function ratearPorUnidade(
  custos: readonly CustoFixo[],
  vendasMensaisEsperadas: number | null,
): Rateio {
  const total = totalMensalCents(custos);
  if (total <= 0) return { definido: false, motivo: 'SEM_CUSTOS' };
  if (!vendasMensaisEsperadas || vendasMensaisEsperadas <= 0) {
    return { definido: false, motivo: 'SEM_VOLUME' };
  }

  return {
    definido: true,
    porUnidadeCents: Math.ceil(total / vendasMensaisEsperadas),
    totalMensalCents: total,
    vendasMensaisEsperadas,
  };
}

/**
 * Quantas unidades o mês precisa vender para os custos fixos se pagarem, dada a
 * margem de contribuição de cada venda.
 *
 * É a pergunta que o rateio esconde: "com esta margem, quantas vendas até parar
 * de perder dinheiro?" Devolve `null` quando a margem não é positiva — aí não
 * existe volume que resolva, e mostrar um número gigante sugeriria que existe.
 */
export function unidadesParaCobrirFixos(
  totalMensalCents: number,
  margemContribuicaoUnitariaCents: number,
): number | null {
  if (totalMensalCents <= 0) return 0;
  if (margemContribuicaoUnitariaCents <= 0) return null;
  return Math.ceil(totalMensalCents / margemContribuicaoUnitariaCents);
}
